import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';

export type CustomStep = {
  from: number;
  to: number;
  step: number;
  labelGap?: number;
  hideStartingLabel?: boolean;
  hideEndingLabel?: boolean;
  hideLabelOnly?: boolean;
  considerFromValueAsUndefined?: boolean;
  considerToValueAsUndefined?: boolean;
  customLabels?: { [key: number]: string };
};

export class Tick {
  constructor(
    public step: number,
    public position: number,
    public value: number,
    public label: string
  ) {}
}

export class AvailableRange {
  constructor(
    public start: number | undefined,
    public end: number | undefined
  ) {}
}

export class SlidersPair {
  value: string;
  first: { index: number; elRef: ElementRef };
  second: { index: number; elRef: ElementRef };
}

@Component({
  selector: 'mpro-range-slider',
  templateUrl: './range-slider.component.html',
  styleUrls: ['./range-slider.component.scss'],
})
export class RangeComponent implements OnChanges, OnInit, AfterViewInit {
  @Input() public doubleSlider?: boolean = false; // tweaks needed to support more than 2
  @Input() public min: number;
  @Input() public max: number;
  // default step; custom ticks are added additionally between the default steps
  @Input() public step: number = 1;
  // by default each 4th default tick will have a label
  @Input() public labelGap: number = 4;
  // TODO implement
  @Input() public customTicks: Tick[] = [];
  @Input() public disabled: boolean;
  @Input() public arrows: boolean = false;
  @Input() public input?: number[];
  @Input() public dragBar: boolean = true;
  @Input() public bigCircle: boolean = false;
  @Input() public clickableTicks: boolean = false;
  @Input() public customSteps: CustomStep[] = [];
  @Input() public labelFormat: (label: string) => string;
  @Input() public sliderFormat: (sliderValue: string) => string;
  @Input() public valueFormat: (val: number) => number | undefined = (val) =>
    val;
  @Input() public minInterval: number = 1;
  @Input() public authRanges?: {
    start: number | undefined;
    end: number | undefined;
  }[];
  @Input() public disabledYears?: number[];
  @Input() public dynamicRange = false;
  @Input() public emitErrorWhenNotAllowed = false;

  @Output() public errorCallback: EventEmitter<{
    isError: boolean;
    currentValue: number;
  }> = new EventEmitter();
  @Output() public valueChanged: EventEmitter<(number | undefined)[]> =
    new EventEmitter();
  @Output() public errorChanged: EventEmitter<string | undefined> =
    new EventEmitter();

  @ViewChildren('inputWrapper')
  public inputWrappers: QueryList<ElementRef>;

  @ViewChild('timeline')
  public timeline: ElementRef;

  @ViewChildren('timelineHighlight')
  public timelineHighlights: QueryList<ElementRef>;

  @ViewChildren('collapsedOverlay')
  public collapsedOverlays: QueryList<ElementRef>;

  public ticks: Tick[] = [];
  public inputs: number[] = [];
  public inputsDisplayValue: string[] = [];
  public inputWrapperPairs: SlidersPair[] = [];

  private slidersCount: number = 1;
  private numberOfDefaultSteps: number;
  private timelineWidth: number;
  private inputWrapperWidth: number;
  private pixelsPerStep: number;
  // store current steps;
  // during drag the event fires constantly, but we need to act only if the step treshold is passed
  private currentSteps: number[] = [];
  private elementXPosition: number = 0;
  private click: (() => void)[] = [];
  private mousedown: (() => void)[] = [];
  private mousemove: () => void = () => {};
  private mouseUp: () => void = () => {};
  private minStep: number = 1;
  private error: string | undefined = undefined;
  private init: boolean = false;
  private isMouseDown: boolean;

  private firstValue: number;
  private secondValue: number;

  private mouseDownUnlistener: () => void;
  private mouseMoveUnlistener: () => void;
  private mouseUpUnlistener: () => void;

  constructor(private renderer: Renderer2, private cdr: ChangeDetectorRef) {}

  public get sliders(): number[] {
    return Array(this.slidersCount)
      .fill(0)
      .map((_, i) => i);
  }

  public get timelineHighlightsCount(): number[] {
    return Array(Math.floor(this.slidersCount / 2)).fill(0);
  }

  public ngOnInit(): void {
    this.slidersCount = this.doubleSlider ? 2 : 1;
    this.initValues();
    this.firstValue = this.min;
    this.secondValue = this.max;
  }

  public ngAfterViewInit(): void {
    this.setView();
  }

  private setView() {
    this.init = false;
    this.resetDraggable();
    this.moveInputsToValue();
    this.inputWrapperPairs = this.getInputWrapperPairs();
    this.cdr.detectChanges();
    setTimeout(() => {
      this.init = true;
      this.moveInputsToValue();

      this.collapsedOverlays.forEach((_el, index) => {
        this.mouseDownUnlistener = this.renderer.listen(
          this.collapsedOverlays.get(index)?.nativeElement,
          'mousedown',
          (event) => {
            if (event.button !== 0) {
              return;
            }
            this.mouseMoveUnlistener = this.renderer.listen(
              document,
              'mousemove',
              (event) => {
                this.moveInput(event.clientX - this.elementXPosition, index);
                this.moveInput(
                  event.clientX - this.elementXPosition,
                  index + 1
                );
              }
            );

            this.mouseUpUnlistener = this.renderer.listen(
              document,
              'mouseup',
              () => {
                this.mouseMoveUnlistener();
                this.mouseUpUnlistener();
              }
            );
          }
        );
      });
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.input && !changes.input.isFirstChange()) {
      this.initValues();
      this.setView();
    }

    //Re-initialize component with new Start Year whenever min Input changes.
    if (
      changes?.min?.currentValue !== changes?.min?.previousValue &&
      !changes.min.isFirstChange()
    ) {
      this.initValues();
      this.setView();
    }

    if (changes.customSteps && !changes.customSteps.isFirstChange()) {
      this.initDefaultTicks();
      this.setView();
    }
  }

  public formatSliderLabel() {
    this.inputsDisplayValue = this.inputs.map((input) =>
      this.sliderFormat ? this.sliderFormat(input.toString()) : input.toString()
    );
  }

  public resetDraggable(): void {
    this.resetPxs();
    this.makeDraggable(this.inputWrappers, ['.drag-bar', '.circle', '.input']);
  }

  private attachDrag(dragTriggerElement: any, inputWrapperIndex: number) {
    if (dragTriggerElement.localName === 'input') {
      this.click.push(
        this.renderer.listen(dragTriggerElement, 'click', (event) =>
          event.target.focus()
        )
      );
    }
    this.mousedown.push(
      this.renderer.listen(dragTriggerElement, 'mousedown', (event) => {
        if (event.button !== 0) {
          return;
        }
        this.isMouseDown = true;
        event.preventDefault(); // this prevents the :active state and affects styling
        this.mousemove = this.renderer.listen(
          document,
          'mousemove',
          (event) => {
            this.moveInput(
              event.clientX - this.elementXPosition,
              inputWrapperIndex
            );
          }
        );
        this.mouseUp = this.renderer.listen(document, 'mouseup', (event) => {
          if (this.isMouseDown) {
            this.isMouseDown = false;
            this.moveInput(
              event.clientX - this.elementXPosition,
              inputWrapperIndex
            );
          }
        });
      })
    );
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event: any) {
    this.resetPxs();
    this.moveInputsToValue();
  }

  @HostListener('document:mouseup', ['$event'])
  public onMouseUp(event: MouseEvent) {
    // clear tracker
    this.mouseUp();
    this.mousemove();
  }

  public showTick(step: number): boolean {
    if (this.customTicks.length <= 0) {
      return true;
    }

    const currentRange = this.getCurrentRange(this.ticks[step].value);

    return currentRange.hideLabelOnly !== true;
  }

  private initValues() {
    this.inputs = [];
    // initialize default values for the sliders
    if (!this.input || this.input.length === 0) {
      this.inputs.push(this.min);
      if (this.slidersCount === 2) {
        this.inputs.push(this.max);
      }
    } else {
      const inputs: number[] = this.input; // enforce type
      this.sliders.forEach((index) => {
        const defalutValue =
          inputs[index] !== undefined
            ? inputs[index]
            : inputs[index - 1] + this.step;
        this.inputs.push(defalutValue);
      });
    }
    this.formatSliderLabel();
  }

  private isValueWithinRange(
    value: number,
    inputWrapperIndex: number
  ): boolean {
    if (!this.doubleSlider) {
      return value >= this.min && value <= this.max;
    }

    const otherIndex = inputWrapperIndex === 0 ? 1 : 0;
    const otherValue = this.inputs[otherIndex];

    if (inputWrapperIndex === 0) {
      return value < otherValue && value >= this.min;
    } else {
      return value > otherValue && value <= this.max;
    }
  }

  private resetPxs() {
    this.numberOfDefaultSteps = Math.ceil((this.max - this.min) / this.minStep);
    this.inputWrapperWidth =
      this.inputWrappers.first.nativeElement.getBoundingClientRect().width;
    this.elementXPosition =
      this.timeline.nativeElement.getBoundingClientRect().left;
    this.timelineWidth =
      this.timeline.nativeElement.getBoundingClientRect().width;
    this.pixelsPerStep = this.timelineWidth / this.numberOfDefaultSteps;

    this.initDefaultTicks();
  }

  private getCurrentRange(value: number): CustomStep {
    for (let index = 0; index < this.customSteps.length; index++) {
      const element = this.customSteps[index];
      if (element.from <= value && value <= element.to) {
        return element;
      }
    }

    return {
      from: this.min,
      to: this.max,
      step: this.step,
      labelGap: this.labelGap,
    };
  }

  private initDefaultTicks() {
    if (this.ticks.length > 0) {
      this.ticks = [];
    }
    if (this.customTicks.length > 0) {
      let prevPosition = 0;
      for (let i = 0; i < this.customTicks.length; i++) {
        const value = this.customTicks[i].value;
        const label = this.customTicks[i].label
          ? this.labelFormat(this.customTicks[i].label)
          : '';
        this.ticks.push(
          new Tick(
            i,
            prevPosition + this.customTicks[i].position * this.timelineWidth,
            value,
            label
          )
        );
        prevPosition += this.customTicks[i].position * this.timelineWidth;
      }
      return;
    }
    this.customSteps.forEach((customStep) => {
      const from = Math.max(this.min, customStep.from);
      const to = Math.min(this.max, customStep.to);
      for (let i = from; i <= to; i += customStep.step) {
        let label =
          customStep.customLabels && customStep.customLabels[i]
            ? customStep.customLabels[i]
            : '';

        if (i === from && customStep.hideStartingLabel) {
          label = '';
        }

        if (i === to && customStep.hideEndingLabel) {
          label = '';
        }

        const position = (i - this.min) * this.pixelsPerStep;

        this.ticks.push(new Tick(i, position, i, label));
      }
    });
  }

  private moveInput(posX: number, inputWrapperIndex: number) {
    if (this.customTicks.length > 0) {
      let epsilon = 5;
      if (
        this.currentSteps[inputWrapperIndex] > 0 &&
        Math.abs(
          this.ticks[this.currentSteps[inputWrapperIndex] - 1].position - posX
        ) < epsilon
      ) {
        this.updateSliderValueAndPosition(
          inputWrapperIndex,
          this.ticks[this.currentSteps[inputWrapperIndex] - 1].value
        );
      } else if (
        this.currentSteps[inputWrapperIndex] < this.ticks.length - 1 &&
        Math.abs(
          this.ticks[this.currentSteps[inputWrapperIndex] + 1].position - posX
        ) < epsilon
      ) {
        this.updateSliderValueAndPosition(
          inputWrapperIndex,
          this.ticks[this.currentSteps[inputWrapperIndex] + 1].value
        );
      }
      return;
    }
    let currentStep = this.min + Math.round(posX / this.pixelsPerStep);
    // Make sure to select the end values when the mouse moves past the slider
    if (currentStep < 0) {
      currentStep = 0;
    }
    if (currentStep >= this.max) {
      currentStep = this.max;
    }
    const value = this.ticks.find(
      (tick) =>
        tick.value ===
        this.findClosestValue(
          this.ticks.map((map) => map.value),
          currentStep
        )
    )?.value;
    if (
      value === undefined ||
      currentStep === this.currentSteps[inputWrapperIndex]
    ) {
      return;
    }
    this.updateSliderValueAndPosition(inputWrapperIndex, value);
  }

  public findClosestValue(arr, target) {
    return arr.reduce((prev, curr) =>
      Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
    );
  }

  public isNumberKey(event: any, slidersPair?: SlidersPair) {
    var charCode = event.which ? event.which : event.keyCode;

    // enter
    if (charCode === 13) {
      return this.parseInputValue(event, slidersPair);
    }

    if (slidersPair && charCode === 45) {
      return true;
    }

    if (charCode < 48 || charCode > 57) {
      return false;
    }

    return true;
  }

  public parseInputValue(event: any, slidersPair?: SlidersPair) {
    if (slidersPair) {
      const pairValue = slidersPair.value;
      const parts = pairValue.split('-').filter((part) => part);
      if (parts.length !== 2) {
        return false;
      }

      this.inputs[slidersPair.first.index] = +parts[0];
      this.inputs[slidersPair.second.index] = +parts[1];
    } else {
      this.inputs = this.inputsDisplayValue.map(
        (input) => Number(input.match(/\d+/)?.[0]) || 0
      );
    }

    // ensure the value is in the allowed range
    this.inputs = this.inputs.map((val) => this.bindValToRange(+val));
    this.moveInputsToValue();

    this.formatSliderLabel();
    return true;
  }

  public setToValue(val: number, inputWrapperIndex: number) {
    this.updateSliderValueAndPosition(inputWrapperIndex, val);
  }

  private moveInputsToValue(): void {
    this.inputs.map((val, index) => this.setToValue(val, index));
  }

  private bindValToRange(input: number) {
    if (input < this.min) {
      return this.min;
    } else if (input > this.max) {
      return this.max;
    }

    return input;
  }

  /**
   * @param coeff etiher 1(right) or -1(left) depending on which arrow was clicked
   */
  public onArrowClick(inputWrapperIndex: number, coeff: number) {
    if (this.disabled) {
      return;
    }

    const currentRange = this.getCurrentRange(
      this.ticks[this.currentSteps[inputWrapperIndex]]?.value ||
        this.currentSteps?.[0]
    );

    const newStepValue =
      this.currentSteps[inputWrapperIndex] + currentRange.step * coeff;
    let newTick = this.getTickAtValue(newStepValue);

    if (!newTick) {
      newTick =
        coeff === 1 ? this.ticks[this.ticks.length - 1] : this.getLowestValue();
    }

    this.updateSliderValueAndPosition(inputWrapperIndex, newTick.value);
  }

  public onTimelineClick(event: any) {
    if (this.disabled) {
      event.stopPropagation();
      return;
    }

    if (this.slidersCount === 1) {
      this.moveInput(event.clientX, 0);
    }
  }

  private getLowestValue(): Tick {
    return this.ticks.reduce(
      (min, item) => (item.value < min.value ? item : min),
      this.ticks[0]
    );
  }

  private getTickAtValue(value: number): Tick {
    if (value <= this.min) {
      return this.getLowestValue();
    }

    if (value >= this.max) {
      return this.ticks[this.ticks.length - 1];
    }

    return (
      this.ticks.find((tick) => tick.value === value) || this.getLowestValue()
    );
  }

  private updateSliderValueAndPosition(
    inputWrapperIndex: number,
    value: number
  ): void {
    const inputWrapperElement =
      this.inputWrappers.get(inputWrapperIndex)?.nativeElement;
    let val = this.calculateSliderPosition(value);
    if (this.isValueWithinRange(val, inputWrapperIndex)) {
      const tickValue = this.calculateSliderPosition(val);
      const newTick = this.getTickAtValue(tickValue);
      this.currentSteps[inputWrapperIndex] = newTick.step;
      this.inputs[inputWrapperIndex] = newTick.value;
      this.renderer.setStyle(
        inputWrapperElement,
        'left',
        `${newTick.position - this.inputWrapperWidth / 2}px`
      );
      this.firstValue = value;
    }
    if (this.init) {
      this.updateInterval();
      this.checkIfAllowedValuesAndInterval();
      this.errorChanged.emit(this.error);
      this.emitChange();
    }
  }

  private updateInterval() {
    if (this.inputWrapperPairs.length) {
      this.inputWrapperPairs.forEach((pair) => {
        this.firstValue =
          this.ticks.find(
            (tick) => tick.value === this.currentSteps[pair.first.index]
          )?.value || 0;
        this.secondValue =
          this.ticks.find(
            (tick) => tick.value === this.currentSteps[pair.second.index]
          )?.value || 1;
      });
      this.updateCollapsedPairs();
      this.updateTimelineHighlight();
    }
  }

  private calculateSliderPosition(value: number) {
    const bindValueToCustomStep = (value: number, range: CustomStep) => {
      let returnValue = Math.round(value / range.step) * range.step;

      if (returnValue - range.from < range.step) {
        returnValue = range.from;
      } else if (returnValue > range.to) {
        returnValue = range.to;
      }

      return returnValue;
    };

    let tickValue: number;
    const currentRange = this.getCurrentRange(value);
    tickValue = bindValueToCustomStep(value, currentRange);
    const newRange = this.getCurrentRange(tickValue);
    tickValue = bindValueToCustomStep(tickValue, newRange);

    return tickValue;
  }

  private updateTimelineHighlight() {
    if (this.slidersCount > 1) {
      this.inputWrapperPairs.forEach((pair, i) => {
        const timelineHighlightElement =
          this.timelineHighlights.get(i)?.nativeElement;
        if (timelineHighlightElement) {
          let left = pair.first.elRef.nativeElement.offsetLeft;
          let width = pair.second.elRef.nativeElement.offsetLeft - left;
          if (width < 0) {
            // sliders overlapped - reverse the timeline highlight
            left = left + width;
            width = Math.abs(width);
          }
          const isValidInterval = this.isIntervalAllowed(
            this.firstValue,
            this.secondValue
          );
          if (!isValidInterval) {
            this.renderer.addClass(timelineHighlightElement, 'not-allowed');
          } else {
            this.renderer.removeClass(timelineHighlightElement, 'not-allowed');
          }
          this.renderer.setStyle(
            timelineHighlightElement,
            'left',
            `${left + this.inputWrapperWidth / 2}px`
          );
          this.renderer.setStyle(
            timelineHighlightElement,
            'width',
            `${width}px`
          );
        }
      });
    }
  }

  private makeDraggable(
    elements: QueryList<ElementRef>,
    dragTriggerClasses: string[]
  ) {
    this.click.forEach((listener) => listener());
    this.mousedown.forEach((listener) => listener());
    elements
      .map((el) => el.nativeElement)
      .forEach((elNative, elIndex) => {
        dragTriggerClasses
          .map((dragTriggerClass) => elNative.querySelector(dragTriggerClass))
          .filter((x) => x) // filter out possible empty values
          .forEach((dragTriggerElement) =>
            this.attachDrag(dragTriggerElement, elIndex)
          );
      });
  }

  private getInputWrapperPairs(): SlidersPair[] {
    return this.inputWrappers.reduce(
      (initial: SlidersPair[], _, index, array) => {
        if ((index + 1) % 2 === 0) {
          initial.push({
            value: `${array[index - 1]}-${array[index]}`,
            first: { index: index - 1, elRef: array[index - 1] },
            second: { index: index, elRef: array[index] },
          });
        }

        return initial;
      },
      []
    );
  }

  private updateCollapsedPairs(): void {
    this.inputWrapperPairs.forEach((pair, index) => {
      pair.value = `${this.firstValue}-${this.secondValue}`;
      const firstSlider = +pair.first.elRef.nativeElement.style.left.slice(
        0,
        -2
      );
      const secondSlider = +pair.second.elRef.nativeElement.style.left.slice(
        0,
        -2
      );
      const overlay = this.collapsedOverlays.get(index)?.nativeElement;
      if (firstSlider + this.inputWrapperWidth > secondSlider) {
        this.renderer.setStyle(
          overlay,
          'width',
          `${1 + (secondSlider + this.inputWrapperWidth - firstSlider)}px`
        );
        this.renderer.setStyle(overlay, 'left', `${firstSlider}px`);
        this.renderer.addClass(overlay, 'show');
      } else {
        this.renderer.removeClass(overlay, 'show');
      }
      const isValidInterval = this.isIntervalAllowed(
        this.firstValue,
        this.secondValue
      );
      if (!isValidInterval) {
        this.renderer.addClass(overlay, 'not-allowed');
      } else {
        this.renderer.removeClass(overlay, 'not-allowed');
      }
    });
  }

  private isIntervalAllowed(start: number, end: number) {
    for (let index = start; index < end; index++) {
      if (!this.isValueAllowed(index)) {
        return false;
      }
    }

    return true;
  }

  private isValueAllowed(value: number) {
    if (this.disabledYears?.includes(value)) {
      return false;
    }

    if (!this.authRanges || this.authRanges.length === 0) {
      return true;
    }

    for (let index = 0; index < this.authRanges.length; index++) {
      const authRange = this.authRanges[index];

      if (authRange.start === undefined && authRange.end === undefined) {
        return true;
      }

      if (authRange.start === undefined) {
        return value <= <number>authRange.end;
      }

      if (authRange.end === undefined) {
        return value >= authRange.start;
      }

      if (value >= authRange.start && value <= authRange.end) {
        return true;
      }
    }

    return false;
  }

  private checkIfAllowedValuesAndInterval(): void {
    this.error = undefined;
    this.inputWrappers.forEach((inputWrapper, i) => {
      const currentValue = this.firstValue;
      const isAllowed = this.isValueAllowed(this.firstValue);
      if (!isAllowed) {
        if (this.emitErrorWhenNotAllowed) {
          this.errorCallback.emit({
            isError: true,
            currentValue,
          });
          this.inputs[i] = this.ticks[this.currentSteps[0]]?.value;
          this.error = `The selected value '${currentValue}' is not allowed by your current access plan.`;
          return;
        }
        this.renderer.addClass(inputWrapper.nativeElement, 'not-allowed');
        this.error = `The selected value '${currentValue}' is not allowed by your current access plan. Allowed values are: '${this.getAuthRanges()}'`;
      } else {
        this.renderer.removeClass(inputWrapper.nativeElement, 'not-allowed');
      }
    });

    if (this.slidersCount > 1) {
      this.inputWrapperPairs.forEach((pair, i) => {
        const start = this.firstValue;
        const end = this.secondValue;
        const isValidInterval = this.isIntervalAllowed(start, end);
        const timelineHighlightElement =
          this.timelineHighlights.get(i)?.nativeElement;
        if (timelineHighlightElement) {
          if (!isValidInterval) {
            this.renderer.addClass(timelineHighlightElement, 'not-allowed');
            this.error = `The selected interval '${start}-${end}' is not allowed by your current access plan. Allowed values are: '${this.getAuthRanges()}'`;
          } else {
            this.renderer.removeClass(timelineHighlightElement, 'not-allowed');
          }
        }

        const overlay = this.collapsedOverlays.get(i)?.nativeElement;
        if (overlay) {
          if (!isValidInterval) {
            this.renderer.addClass(overlay, 'not-allowed');
          } else {
            this.renderer.removeClass(overlay, 'not-allowed');
          }
        }
      });
    }
  }

  private getAuthRanges(): string {
    const strRanges = this.authRanges?.map((authRange) => {
      const min = this.labelFormat
        ? this.labelFormat(this.min.toString())
        : this.min;
      const max = this.labelFormat
        ? this.labelFormat(this.max.toString())
        : this.max;
      return `${authRange.start || min}-${authRange.end || max}`;
    });
    if (strRanges) {
      return strRanges.join(', ');
    }

    return '';
  }

  private emitChange(): void {
    // sort output to avoid the value of the second slider to be greater than the value of the first slider
    const res = this.inputs
      .slice()
      .sort((a, b) => a - b)
      .map((v) => this.valueFormat(v));
    this.valueChanged.emit(res);
  }

  public clickTick(tick: Tick) {
    if (this.disabled || !this.clickableTicks) {
      return;
    }

    this.updateSliderValueAndPosition(0, tick.value);
  }
}
